/* eslint-disable react/no-array-index-key */
import {autoFillCode, getNewFormMedicationProfiles} from '@/modules/drug-catalog/functions'
import {useMedicationProfileForm} from '@/modules/drug-catalog/hooks/use-medication-profile-form'
import {useNavigateBackToTradeNameForm} from '@/modules/drug-catalog/hooks/use-navigate-back-to-trade-name-form'
import {mapMedicationProfileFormToApiValues} from '@/modules/drug-catalog/medication-profiles/functions'
import {useGetActiveSubstanceDrug} from '@/modules/drug-catalog/substances/api'
import {AlternativeCodesTab} from '@/modules/drug-catalog/substances/components/active-substance-form/alternative-codes-tab'
import {DosingSafetyTab} from '@/modules/drug-catalog/substances/components/active-substance-form/dosing-safety-tab'
import {EssentialSetupTab} from '@/modules/drug-catalog/substances/components/active-substance-form/essential-setup-tab'
import {PropertiesHandlingTab} from '@/modules/drug-catalog/substances/components/active-substance-form/properties-handling-tab'
import {getSubstanceFormDefaultValues} from '@/modules/drug-catalog/substances/functions'
import {ActiveSubstanceFormSchema} from '@/modules/drug-catalog/substances/schemas'
import {useGetSettings} from '@/modules/settings/api'
import {useGetExistingCompoundMapping} from '@/modules/smart-compounders/api/use-get-existing-compound-mapping'
import {SmartCompounderSection} from '@/modules/smart-compounders/components/smart-compounder-section'
import {FLAGS} from '@/shared/constants'
import {useZero} from '@/shared/integrations/zero/use-zero'
import {Box, HStack, Tabs} from '@chakra-ui/react'
import {CompoundMappingType} from '@cypro/engine/database/schema'
import {LinkButton} from '@cypro/ui/components/ui/link-button'
import {LoadingLogo} from '@cypro/ui/components/ui/loading-logo'
import {useBlockerDialog} from '@cypro/ui/hooks/use-blocker-dialog'
import {useGuardNonexistentResourceForm} from '@cypro/ui/hooks/use-guard-nonexistent-resource-form'
import {setInitialFieldValues, useAppForm} from '@cypro/ui/integrations/form'
import {toaster} from '@cypro/ui/toaster'
import {getFirstElementOrSelf, isDefined, removeElementAt} from '@cypro/ui/utils'
import {revalidateLogic, useStore} from '@tanstack/react-form'
import {useNavigate} from '@tanstack/react-router'
import {useTranslate} from '@tolgee/react'
import {SubmitEvent, useEffect, useState} from 'react'
const TABS = [
  'essential-setup',
  'dosing-safety',
  'properties-handling',
  'alternative-codes',
  'smart-compounder',
] as const
type TabId = (typeof TABS)[number]

interface Props {
  substanceId: string | undefined
}

export function ActiveSubstanceForm({substanceId}: Props) {
  const {t} = useTranslate('common')

  const zero = useZero()
  const navigate = useNavigate()
  const backToTradeName = useNavigateBackToTradeNameForm()

  const [activeSubstance, queryState] = useGetActiveSubstanceDrug(substanceId)
  const {settings} = useGetSettings()

  const isEditing = Boolean(substanceId)
  const [activeTab, setActiveTab] = useState<TabId>('essential-setup')

  const form = useAppForm({
    defaultValues: getSubstanceFormDefaultValues(activeSubstance, settings),
    validators: {
      onDynamic: ActiveSubstanceFormSchema,
    },
    validationLogic: revalidateLogic(),
    onSubmit({value}) {
      const payload: Parameters<typeof zero.mutators.activeSubstances.add>[0] = {
        ...value,
        doseCalculationMethod: getFirstElementOrSelf(value.doseCalculationMethod),
        doseUnitId: getFirstElementOrSelf(value.doseUnitId),
        roundingMode: getFirstElementOrSelf(value.roundingMode),
        minIntervalUnit: getFirstElementOrSelf(value.minIntervalUnit),
        overdosePreventionLevel: getFirstElementOrSelf(value.overdosePreventionLevel),
        preparationType: getFirstElementOrSelf(value.preparationType),
        medicationProfiles: value.medicationProfiles.map(mapMedicationProfileFormToApiValues),
        machineId: FLAGS.smartCompounder ? value.machineId : undefined,
      }

      if (substanceId) {
        return editActiveSubstance({...payload, id: substanceId})
      }

      return createActiveSubstance(payload)
    },
    listeners: {
      onChange({fieldApi, formApi}) {
        if (
          fieldApi.name === 'molarMassFreeBase' ||
          fieldApi.name === 'molarMassSaltBase' ||
          fieldApi.name === 'precision'
        ) {
          const {molarMassFreeBase, molarMassSaltBase, precision = 0} = formApi.state.values
          if (isDefined(molarMassFreeBase) && isDefined(molarMassSaltBase)) {
            const saltFactor = Number((molarMassFreeBase / molarMassSaltBase).toFixed(precision))
            formApi.setFieldValue('saltFactor', Number.isFinite(saltFactor) ? saltFactor : 0, {dontRunListeners: true})
          }
        }

        if (fieldApi.name === 'saltFactor') {
          form.setFieldValue('molarMassFreeBase', 0, {dontRunListeners: true})
          form.setFieldValue('molarMassSaltBase', 0, {dontRunListeners: true})
        }

        if (!isEditing && fieldApi.name === 'name') {
          const isCodePristine = Boolean(formApi.fieldInfo.code?.instance?.state.meta.isPristine)
          const {name, code} = formApi.state.values
          const autoFilled = autoFillCode({name, code, isCodePristine})
          if (!isDefined(autoFilled)) return
          formApi.setFieldValue('code', autoFilled, {dontRunListeners: true, dontUpdateMeta: true})
        }
      },
    },
  })

  const {onAddMedicationProfile, onEditMedicationProfile} = useMedicationProfileForm({
    onSubmit: (value, meta) => {
      form.setFieldValue(
        'medicationProfiles',
        getNewFormMedicationProfiles(form.state.values.medicationProfiles, value, meta.index)
      )
    },
  })

  const isDefaultValue = useStore(form.store, state => state.isDefaultValue)

  useBlockerDialog({shouldBlock: !isDefaultValue})
  useGuardNonexistentResourceForm({resource: activeSubstance, queryState, shouldGuard: isEditing})

  const existingMapping = useGetExistingCompoundMapping(CompoundMappingType.enum.DRUG, substanceId)

  useEffect(() => {
    if (existingMapping) {
      setInitialFieldValues(form, {machineId: existingMapping.machineId ?? ''})
    }
  }, [existingMapping, form])

  function createActiveSubstance(payload: Parameters<typeof zero.mutators.activeSubstances.add>[0]) {
    return zero.attemptMutation(zero.mutators.activeSubstances.add(payload), async mutation => {
      const data = await mutation.server
      if (!data) return

      toaster.success({title: t('success'), description: t('active_substance_created_successfully')})

      if (await backToTradeName()) return

      form.reset()
      setActiveTab(TABS[0])
    })
  }

  function editActiveSubstance(payload: Parameters<typeof zero.mutators.activeSubstances.update>[0]) {
    return zero.attemptMutation(zero.mutators.activeSubstances.update(payload), async mutation => {
      const data = await mutation.server
      if (!data) return

      toaster.success({title: t('success'), description: t('active_substance_updated_successfully')})

      if (await backToTradeName()) return

      navigate({to: '/configuration/drug-catalog/active-substances', ignoreBlocker: true})
    })
  }

  function onSubmit(event: SubmitEvent) {
    event.preventDefault()
    form.handleSubmit()
  }

  function onDeleteMedicationProfile(index: number) {
    form.setFieldValue('medicationProfiles', removeElementAt(form.state.values.medicationProfiles, index))
  }

  if (queryState.type !== 'complete') return <LoadingLogo />

  const submitLabel = !isEditing ? t('create_substance') : t('save_changes')

  return (
    <form onSubmit={onSubmit}>
      <form.AppForm>
        <Tabs.Root value={activeTab} onValueChange={changes => setActiveTab(changes.value as TabId)}>
          <form.ErrorObserver />

          <Tabs.List>
            <Tabs.Trigger value="essential-setup">
              {t('essential_setup')}
              <Box as="span" color="fg.error" marginLeft="1">
                *
              </Box>
            </Tabs.Trigger>
            <Tabs.Trigger value="dosing-safety">
              {t('dosing_safety')}
              <Box as="span" color="fg.error" marginLeft="1">
                *
              </Box>
            </Tabs.Trigger>
            <Tabs.Trigger value="properties-handling">{t('properties_handling')}</Tabs.Trigger>
            <Tabs.Trigger value="alternative-codes">{t('alternative_codes')}</Tabs.Trigger>
            {FLAGS.smartCompounder && <Tabs.Trigger value="smart-compounder">{t('smart_compounder')}</Tabs.Trigger>}
          </Tabs.List>

          <Tabs.Content value="essential-setup" paddingTop="6">
            <EssentialSetupTab
              form={form}
              onAddMedicationProfile={onAddMedicationProfile}
              onEditMedicationProfile={onEditMedicationProfile}
              onDeleteMedicationProfile={onDeleteMedicationProfile}
            />
          </Tabs.Content>

          <Tabs.Content value="dosing-safety" paddingTop="6">
            <DosingSafetyTab form={form} drugId={substanceId} />
          </Tabs.Content>

          <Tabs.Content value="properties-handling" paddingTop="6">
            <PropertiesHandlingTab form={form} isEditing={isEditing} />
          </Tabs.Content>

          <Tabs.Content value="alternative-codes" paddingTop="6">
            <AlternativeCodesTab form={form} />
          </Tabs.Content>

          {FLAGS.smartCompounder && (
            <Tabs.Content value="smart-compounder" paddingTop="6">
              {/* @ts-expect-error */}
              <SmartCompounderSection form={form} mappingType={CompoundMappingType.enum.DRUG} />
            </Tabs.Content>
          )}

          <HStack paddingY="6" gapX="4" marginTop="6">
            <LinkButton to="/configuration/drug-catalog/active-substances" variant="subtle">
              {t('cancel')}
            </LinkButton>

            <form.NextStepButton>{t('next_step')}</form.NextStepButton>

            <Box marginLeft="auto">
              <form.SubmitButton disabled={isEditing && isDefaultValue}>{submitLabel}</form.SubmitButton>
            </Box>
          </HStack>
        </Tabs.Root>
      </form.AppForm>
    </form>
  )
}
